import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { Optional } from '../../../../Common/interfaces';
import { updateAt } from '../../../../Common/Utils';
import { Switch, Textarea } from '../../../../components';
import {
    Exercise,
    LabelBody,
    LabelBodyHeadline,
    LabelBodySmall,
    LabelDisplay1,
    LabelHeader4,
    LabelHeader5,
    SecondaryButton,
    Spacer,
    SwirlyBox,
} from '../../components';
import { exercisesAtom } from '../../data';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { IllustrationFlower } from '../../icons';
import { ExerciseStatus } from '../../interfaces';
import { parseArray, useSyncedState } from '../../useSyncedState';
import { defaultActions } from './defaultActions';
import {
    importantAreasKey,
    ratingsImportanceKey,
    ratingsTimeKey,
} from './Module4Exercise1';
import { parseRatings } from './Rating';

const selectedAreasKey = 'selectedAreas';

export const exerciseData42: ExerciseData = {
    ExerciseView: Module4Exercise2,
    dataKeys: [selectedAreasKey],
    questions: [
        {
            dataKeys: [selectedAreasKey],
            summary(exercise) {
                const data = parseSelectedAreas(
                    exercise.Data[selectedAreasKey],
                );
                return data.map((x) => x.text);
            },
            Answers({ exercise, ...props }) {
                const data = parseSelectedAreas(
                    exercise.Data[selectedAreasKey],
                );
                return (
                    <div {...props}>
                        <LabelDisplay1>Dine handlinger</LabelDisplay1>
                        <LabelBody class="mt-10">
                            Hvordan vil du skabe mere balance?
                        </LabelBody>
                        {data.map((x) => (
                            <div key={x.name} className="mt-20">
                                <LabelBodyHeadline color="text-dark-red">
                                    {x.name}
                                </LabelBodyHeadline>
                                <LabelBody class="whitespace-pre-wrap">
                                    “{x.text.trim()}”
                                </LabelBody>
                            </div>
                        ))}
                    </div>
                );
            },
        },
    ],
};

type ImportantArea = {
    name: string;
    importance: number;
    time: number;
};

type SelectedArea = {
    name: string;
    text: string;
};

function parseSelectedAreas(json: Optional<string>): SelectedArea[] {
    return JSON.parse(json ?? '[]');
}
function stringifySelectedAreas(areas: SelectedArea[]): string {
    return JSON.stringify(areas);
}

function Module4Exercise2({ exercise, onContinue }: ExerciseViewProps) {
    const exercise1 = useAtomValue(exercisesAtom).find((x) => x.Code === '4.1');
    const importantAreas = useMemo(
        () => parseArray(exercise1?.Data[importantAreasKey]),
        [exercise1?.Data[importantAreasKey]],
    );
    const areas: ImportantArea[] = useMemo(() => {
        const ratingsTime = parseRatings(exercise1?.Data[ratingsTimeKey]);
        const ratingsImportance = parseRatings(
            exercise1?.Data[ratingsImportanceKey],
        );

        return importantAreas.map((name) => ({
            name,
            time: ratingsTime.find((x) => x.name === name)?.rating ?? 2,
            importance:
                ratingsImportance.find((x) => x.name === name)?.rating ?? 2,
        }));
    }, [
        importantAreas,
        exercise1?.Data[ratingsTimeKey],
        exercise1?.Data[ratingsImportanceKey],
    ]);
    const selected: SelectedArea[] = useMemo(() => {
        const selected = parseSelectedAreas(exercise.Data[selectedAreasKey]);
        return selected.filter((x) => importantAreas.includes(x.name));
    }, [importantAreas, exercise.Data[selectedAreasKey]]);
    return (
        <Exercise title="Fra værdi til handling">
            <Switch
                cases={{
                    1: (
                        <Page1
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            areas={areas}
                            selected={selected}
                            onContinue={(selected) =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                    data: {
                                        [selectedAreasKey]:
                                            stringifySelectedAreas(selected),
                                    },
                                })
                            }
                        />
                    ),
                    3: (
                        <Page3
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                })
                            }
                        />
                    ),
                }}
                value={exercise.CurrentPage}
                default={1}
            />
        </Exercise>
    );
}

type Page1Props = {
    onContinue: () => void;
};
function Page1({ onContinue }: Page1Props) {
    return (
        <>
            <LabelHeader4>
                Hvordan får jeg mere plads til det der er vigtigt for mig?
            </LabelHeader4>
            <LabelBody className="my-10">
                Nu skal du hjælpe dig selv med, hvad du kan gøre for at skabe
                større balance mellem det, der er vigtigt for dig og hvordan du
                bruger din tid.
            </LabelBody>
            <LabelBody>
                Det er selvfølgelig ikke alle livsområder, som du helt kan ændre
                på.
            </LabelBody>
            <LabelBody className="mt-10">
                Det kan for eksempel være et vilkår at bruge tid på sit arbejde
                for at tjene penge. Men du kan tage stilling til, hvor meget du
                har brug for at tjene og måske justere tiden efter det. Det
                vigtige er, at du forsøger at gøre noget aktivt for at skabe en
                bedre balance
            </LabelBody>
            <SwirlyBox className="my-30">
                Når du er klar til at tilføje dine handlinger, kan du gå videre
            </SwirlyBox>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

type Page2Props = {
    areas: ImportantArea[];
    selected: SelectedArea[];
    onContinue: (selected: SelectedArea[]) => void;
};
function Page2({ areas, selected: _selected, onContinue }: Page2Props) {
    const [selected, setSelected] = useSyncedState(_selected);
    return (
        <>
            <LabelHeader4>Handlinger til kompasset</LabelHeader4>
            <LabelBody className="mt-10">
                Herunder kan du se, hvordan du har vurderet dine livsområder på
                tid og vigtighed. Du skal nu udvælge ét eller to livsområder fra
                dit kompas, som du gerne vil ændre på. Tryk på et livsområde for
                at vælge det.
            </LabelBody>
            <div className="my-30 space-y-20">
                {areas.map((area) => {
                    const isSelected = !!selected.find(
                        (y) => y.name === area.name,
                    );
                    return (
                        <AreaView
                            key={area.name}
                            area={area}
                            isSelected={isSelected}
                            disabled={!isSelected && selected.length >= 2}
                            onChangeIsSelected={(newValue) => {
                                if (newValue === isSelected) return;
                                if (newValue) {
                                    setSelected(
                                        selected.concat({
                                            name: area.name,
                                            text: '',
                                        }),
                                    );
                                } else {
                                    setSelected(
                                        selected.filter(
                                            (x) => x.name !== area.name,
                                        ),
                                    );
                                }
                            }}
                        />
                    );
                })}
            </div>
            {selected.length > 0 && (
                <>
                    <LabelHeader5>Dine handlinger</LabelHeader5>
                    <LabelBody className="my-10">
                        Forsøg nu at finde på en konkret handling, du kan gøre
                        på det livsområde, som du har valgt ud.
                    </LabelBody>
                    <LabelBody>
                        Når det drejer sig om at ville ændre på noget, så er det
                        et godt tip at være konkret og realistisk. Så prøv at
                        gøre handlingen så specifik, som muligt ved at beskrive
                        hvad du vil gøre, hvornår det skal ske og med hvem.
                        F.eks. Jeg vil gå en tur hver mandag sammen med min gode
                        ven.
                    </LabelBody>
                    {selected.map((x, idx) => (
                        <div key={x.name} className="mt-30">
                            <LabelBodyHeadline color="text-dark-red">
                                {x.name}
                            </LabelBodyHeadline>
                            <LabelBody>
                                Hvordan vil du skabe mere balance?
                            </LabelBody>
                            <Textarea
                                className="mt-5"
                                placeholder={
                                    defaultActions.find(
                                        (y) => y.name === x.name,
                                    )?.example
                                }
                                value={x.text}
                                onChangeValue={(text) =>
                                    setSelected(
                                        updateAt(selected, idx, { ...x, text }),
                                    )
                                }
                            />
                        </div>
                    ))}
                </>
            )}
            <Spacer className="mt-30" />
            <SecondaryButton onClick={() => onContinue(selected)}>
                {selected.length === 0 ? 'Fortsæt' : 'Gem og fortsæt'}
            </SecondaryButton>
        </>
    );
}

type AreaViewProps = {
    area: ImportantArea;
    isSelected: boolean;
    onChangeIsSelected: (isSelected: boolean) => void;
    disabled: boolean;
};
function AreaView({
    area,
    isSelected,
    onChangeIsSelected,
    disabled,
}: AreaViewProps) {
    return (
        <div
            className={classNames(
                'flex justify-between rounded-[10px] border p-15',
                {
                    'border-placeholder bg-white': !isSelected,
                    'border-vibrant-blue bg-vibrant-blue-10p': isSelected,
                    'opacity-60': disabled,
                },
            )}
            onClick={() => onChangeIsSelected(!isSelected)}
            inert={disabled ? '' : undefined}
        >
            <LabelBodyHeadline>{area.name}</LabelBodyHeadline>
            <div className="flex">
                <LabelBodySmall className="flex">
                    Vigtighed
                    <LabelBodyHeadline className="ml-10 aspect-square w-[24px] rounded-[5px] bg-dark-blue text-center text-white">
                        {area.importance}
                    </LabelBodyHeadline>
                </LabelBodySmall>
                <LabelBodySmall className="ml-30 flex">
                    Tid
                    <LabelBodyHeadline className="ml-10 aspect-square w-[24px] rounded-[5px] bg-dark-blue text-center text-white">
                        {area.time}
                    </LabelBodyHeadline>
                </LabelBodySmall>
            </div>
        </div>
    );
}

type Page3Props = {
    onContinue: () => void;
};
function Page3({ onContinue }: Page3Props) {
    return (
        <>
            <LabelHeader4>Afrunding</LabelHeader4>
            <LabelBody className="mt-10">
                Vi øver os typisk hele livet på at skabe balance i hverdagen. Så
                det er vigtigt at være opmærksom på, at det ofte tager tid, før
                vi mærker en forandring. Se denne øvelse som et redskab, du
                altid kan vende tilbage til, når det giver mening for dig.
            </LabelBody>
            <IllustrationFlower className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>Afslut</SecondaryButton>
        </>
    );
}
